import { AlertTriangle, Check, Circle, Loader2, MinusCircle, RotateCcw } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { cn } from "@/shared/lib/utils";

import {
  doneSummary,
  stageRows,
  type FlowPhase,
  type StageState,
} from "../../../model/generateFlow";

const ICON: Record<StageState, ReactNode> = {
  done: <Check className="h-3.5 w-3.5 text-success" aria-hidden />,
  active: <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" aria-hidden />,
  failed: <AlertTriangle className="h-3.5 w-3.5 text-destructive" aria-hidden />,
  pending: <Circle className="h-3.5 w-3.5 text-muted-foreground/50" aria-hidden />,
  skipped: <MinusCircle className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />,
};

const STATE_TEXT: Record<StageState, string> = {
  done: "已完成",
  active: "进行中",
  failed: "失败",
  pending: "等待",
  skipped: "跳过",
};

/**
 * 一键生成 / 更新预览的分步进度(读资料 / 写文案 / 配图 x/N / 发布预览)。失败给原因 + 重试;成功给小结 + 下一步。
 * `phase.kind === "idle" | "confirming"` 时不画。
 */
export function GenerateProgress({
  phase,
  title = "正在生成官网",
  onRetry,
  done,
  highlight = false,
}: {
  phase: FlowPhase;
  title?: string;
  onRetry?: () => void;
  /** 成功后的下一步(按钮 / 链接)。 */
  done?: ReactNode;
  /** 运行中加醒目边框(生成页顶部那块)。 */
  highlight?: boolean;
}) {
  if (phase.kind === "idle" || phase.kind === "confirming") return null;
  const task = phase.task;
  const rows = stageRows(task);
  return (
    <section
      aria-label="生成进度"
      data-flow-phase={phase.kind}
      aria-live="polite"
      className={cn(
        "space-y-3 border border-border bg-card p-4",
        highlight && phase.kind === "running" && "border-primary/60 ring-2 ring-primary/30",
        CARD_WALL_RADIUS
      )}
    >
      <h2 className="text-sm font-semibold text-foreground">
        {phase.kind === "succeeded" ? "已完成" : phase.kind === "failed" ? "生成没有完成" : title}
      </h2>
      <ol className="grid gap-2 sm:grid-cols-4" aria-label="生成步骤">
        {rows.map((r) => (
          <li
            key={r.key}
            data-stage={r.key}
            data-state={r.state}
            className={cn(
              "flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs",
              r.state === "active" && "border-primary/50 bg-primary/5",
              r.state === "failed" && "border-destructive/50 bg-destructive/5",
              (r.state === "pending" || r.state === "skipped") && "text-muted-foreground"
            )}
          >
            {ICON[r.state]}
            <span>{r.label}</span>
            <span className="sr-only">{STATE_TEXT[r.state]}</span>
          </li>
        ))}
      </ol>
      {phase.kind === "failed" && (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-2 text-xs">
          <span className="text-destructive">{phase.error}</span>
          {onRetry && (
            <Button size="sm" variant="outline" onClick={onRetry}>
              <RotateCcw />
              重试
            </Button>
          )}
        </div>
      )}
      {phase.kind === "succeeded" && phase.task.warnings?.length > 0 && (
        <ul className="list-disc pl-4 text-2xs text-warning">
          {phase.task.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
      {phase.kind === "succeeded" && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
          <span className="text-muted-foreground">{doneSummary(phase.task)}</span>
          {done}
        </div>
      )}
    </section>
  );
}
